import WidgetLoadingAnimation from 'components/Widgets/WidgetLoadingAnimation';
import WidgetTimeUpdate from 'components/Widgets/WidgetTimeUpdate';
import TimePeriodCard from 'features/Weather/components/TimePeriodCard/TimePeriodCard';
import useLocation from 'hooks/useLocation';
import { useMemo, useState } from 'react';
import { FaPlane } from 'react-icons/fa6';
import { useGetAirportDelayForecastQuery } from 'services/weather/airports/airports.api';
import { formatDateTime } from 'utils/dates/date.utils';
import SpecialReportWidgetSelect from '../../components/SpecialReportWidgetSelect/SpecialReportWidgetSelect';
import SpecialReportWidgetState from '../../components/SpecialReportWidgetState/SpecialReportWidgetState';
import '../../assets/specialReportWidgets.scss';
import { airportDelayDetails } from '../../features/AirportDelays/config/airportDelays.config';
import { getAirportDelayDays } from '../../features/AirportDelays/utils/airportDelays.utils';
import useSpecialReportWidgetRefresh from '../../hooks/useSpecialReportWidgetRefresh';

// Wide widgets put controls beside the forecast; tall widgets leave more room for each control.
const layoutConfig = {
  '1x1': { contentClassName: '', controlsClassName: 'special-report-widget-controls-paired' },
  '1x2': { contentClassName: '', controlsClassName: '' },
  '2x1': { contentClassName: 'special-report-widget-content-wide', controlsClassName: '' },
};

/** Loads airport delays and lets the user choose one airport and forecast period. */
const AirportDelaysWidget = ({ layout = '1x1', dimensions, widgetRefresh }) => {
  const { locations } = useLocation();
  const location = locations.effectiveLocation;
  const isLocationLoading = !location || location.isLoading;
  const { data, isLoading, isFetching, isError, refetch } = useGetAirportDelayForecastQuery(
    location,
    { skip: isLocationLoading },
  );

  useSpecialReportWidgetRefresh({
    route: 'getAirportDelayForecast',
    location,
    widgetRefresh,
    refetch,
    skip: isLocationLoading,
  });

  const [selectedAirportValue, setSelectedAirportValue] = useState('');
  const [selectedPeriodValue, setSelectedPeriodValue] = useState('');
  const config = layoutConfig[layout] ?? layoutConfig['1x1'];
  const airportOptions = useMemo(() => {
    const nameCounts = new Map();
    return (Array.isArray(data) ? data : [])
      .filter((report) => report && typeof report === 'object' && !Array.isArray(report))
      .map((report) => {
        const name = report.location || 'Unnamed airport';
        const occurrence = nameCounts.get(name) ?? 0;
        nameCounts.set(name, occurrence + 1);
        // Count only matching names so reordering other airports does not change this selection.
        return { value: JSON.stringify([name, occurrence]), label: name, report };
      });
  }, [data]);
  // A refreshed response can remove a selection. Fall back to its first available replacement.
  const selectedAirport =
    airportOptions.find((airport) => airport.value === selectedAirportValue) ?? airportOptions[0];
  const periodOptions = useMemo(() => {
    const periodCounts = new Map();
    return getAirportDelayDays(selectedAirport?.report.segments).flatMap(
      ({ key, date, segments }) =>
        segments.map((segment) => {
          const dateLabel = formatDateTime(date, 'short', 'Date unavailable', true);
          const periodKey = JSON.stringify([key, segment.iso8601, segment.day_segment]);
          const occurrence = periodCounts.get(periodKey) ?? 0;
          periodCounts.set(periodKey, occurrence + 1);
          return {
            // Keep duplicate periods selectable without changing other IDs when new periods arrive.
            value: JSON.stringify([periodKey, occurrence]),
            label: `${dateLabel} · ${segment.day_segment || 'Time unavailable'}`,
            dateLabel,
            segment,
          };
        }),
    );
  }, [selectedAirport]);
  const selectedPeriod =
    periodOptions.find((period) => period.value === selectedPeriodValue) ?? periodOptions[0];

  // Keep the selected forecast visible while a background refresh is running.
  if (isLocationLoading || ((isLoading || isFetching) && !data)) {
    return <WidgetLoadingAnimation widgetName="airportDelays" dimensions={dimensions} />;
  }

  return (
    <>
      <WidgetTimeUpdate route="getAirportDelayForecast" realtime compact />
      <section
        className="special-report-widget d-flex flex-column h-100 min-h-0 gap-2 p-2"
        aria-label="Airport delays"
        data-layout={layout}
      >
        <SpecialReportWidgetState
          title="Airport Delays"
          isFetching={isFetching}
          isError={isError}
          hasData={airportOptions.length > 0}
          onRetry={refetch}
          emptyMessage="No airport delay forecasts are available for this location."
        >
          <div className={`special-report-widget-content ${config.contentClassName}`}>
            <div className={`special-report-widget-controls ${config.controlsClassName}`}>
              <SpecialReportWidgetSelect
                label="Airport"
                options={airportOptions}
                selectedValue={selectedAirport?.value}
                onSelect={(value) => {
                  setSelectedAirportValue(value);
                  setSelectedPeriodValue('');
                }}
              />
              {selectedPeriod && (
                <SpecialReportWidgetSelect
                  label="Forecast period"
                  options={periodOptions}
                  selectedValue={selectedPeriod.value}
                  onSelect={setSelectedPeriodValue}
                />
              )}
            </div>
            <div className="special-report-widget-scroll">
              {selectedPeriod ? (
                <TimePeriodCard
                  data={selectedPeriod.segment}
                  title={selectedPeriod.segment.day_segment || 'Time unavailable'}
                  subtitle={selectedPeriod.dateLabel}
                  subtitleIcon={<FaPlane size={32} />}
                  details={airportDelayDetails.filter(
                    (detail) => detail.getValue(selectedPeriod.segment) !== null,
                  )}
                />
              ) : (
                <p className="small m-0 p-2" role="status">
                  No forecast periods are available for this airport.
                </p>
              )}
            </div>
          </div>
        </SpecialReportWidgetState>
      </section>
    </>
  );
};

export default AirportDelaysWidget;
